import React, { CSSProperties } from 'react';
import { List } from 'react-window';
import { Badge, Box, Flex, IconButton, Popover, Text, TextField } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { cx } from 'class-variance-authority';
import { ClearIcon, ExpandMoreIcon } from 'shared/icons';

import { useAutocomplete } from './autocomplete.provider';
import './styles/autocomplete.css';

export const AutocompleteView = ({
   children,
   placeholder,
   disabled = false,
}: {
   children: React.ReactNode[];
   placeholder?: string;
   disabled?: boolean;
}) => {
   const viewModel = useAutocomplete();
   const inputRef = React.useRef<HTMLInputElement | null>(null);

   return (
      <Popover.Root
         open={viewModel.opened}
         onOpenChange={(open) => {
            if (open) {
               inputRef.current?.focus();
            }
            viewModel.onOpenedChange(open);
         }}
      >
         <Popover.Trigger>
            <Flex className="autocomplete__trigger" justify="between" width="100%">
               <TextField.Root
                  size="3"
                  color="blue"
                  ref={inputRef}
                  disabled={disabled}
                  className="autocomplete__search"
                  placeholder={placeholder}
                  value={viewModel.search}
                  onChange={(e) => {
                     viewModel.onSearch(e.target.value);
                  }}
                  onKeyDown={() => {
                     if (!viewModel.opened) {
                        viewModel.onOpenedChange(true);
                     }
                  }}
               >
                  <TextField.Slot side="right">
                     <Text
                        className={cx(
                           'autocomplete__indicator',
                           viewModel.opened && 'autocomplete__indicator--opened'
                        )}
                     >
                        <ExpandMoreIcon width={24} height={24} />
                     </Text>
                  </TextField.Slot>
               </TextField.Root>
            </Flex>
         </Popover.Trigger>
         <Popover.Content
            maxHeight="200px"
            size="1"
            onCloseAutoFocus={(e) => {
               if (!viewModel.value) {
                  viewModel.clearSearch();
               }
               e.preventDefault();
            }}
            onOpenAutoFocus={(e) => {
               e.preventDefault();
            }}
         >
            {children.length > 0 ? (
               <List<{
                  items: React.ReactNode[];
               }>
                  rowComponent={RowComponent}
                  rowCount={children.length}
                  rowHeight={40}
                  rowProps={{ items: children }}
               />
            ) : (
               <Box>
                  <Text color="gray">Опцій не знайдено</Text>
               </Box>
            )}
         </Popover.Content>
      </Popover.Root>
   );
};

function RowComponent({
   index,
   items,
   style,
}: {
   index: number;
   items: React.ReactNode[];
   style: CSSProperties;
}) {
   return (
      <div className="multi-select__row-item" style={style}>
         {items[index]}
      </div>
   );
}

const AutocompleteOptionView = ({
   value,
   children,
}: {
   value: unknown;
   children: React.ReactNode;
}) => {
   const viewModel = useAutocomplete();
   return (
      <div
         className="multi-select__option"
         onClick={() => {
            viewModel.onSelect(value);
            viewModel.onOpenedChange(false);
         }}
      >
         {children}
      </div>
   );
};

export const Autocomplete = observer(AutocompleteView);
export const AutocompleteOption = observer(AutocompleteOptionView);
